import InfoIcon from '@mui/icons-material/Info';
import PowerIcon from '@mui/icons-material/PowerSettingsNew';
import {
  alpha,
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import { green, red } from '@mui/material/colors';
import Dialog from '@mui/material/Dialog';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { trackEvent } from '@sophocles/analytics';
// eslint-disable-next-line no-restricted-imports -- pending migration to useDeleteConfirm (command confirms)
import SimpleTwoButtonPopup from '@sophocles/components/SimpleTwoButtonPopup';
import { getLoadTypeCapabilities, selectRelayLoadTypeCapsById } from '@sophocles/features/reference';
import { DISPATCH_STATUS } from '@sophocles/hooks/commandDispatchResult';
import useCommandDispatch from '@sophocles/hooks/useCommandDispatch';

const RELAY_COMMAND_BY_CHOICE = {
  on: 'RELAY_ON',
  off: 'RELAY_OFF',
  reboot: 'REBOOT_RELAY',
};

function RelayCommandsPopup({
  relay,
  siteId,
  onMobile,
  display: displayOverride,
  stateColor,
  isCommercialUnit,
  onOpenChange,
}) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState(null);
  const [errors, setErrors] = useState([]);
  const { dispatchCommands, submitting, activityLocked } = useCommandDispatch(siteId);
  const loadTypeCapsById = useSelector(selectRelayLoadTypeCapsById);

  const toggleChoice = val => () => setChoice(prev => (prev === val ? null : val));

  const clearErrors = () => setErrors([]);

  const getDescription = () => {
    switch (choice) {
      case 'on':
        return isCommercialUnit ? t('commands.relayOnCommercialDescription') : t('commands.relayOnDescription');
      case 'off':
        return isCommercialUnit ? t('commands.relayOffCommercialDescription') : t('commands.relayOffDescription');
      case 'reboot':
        return t('commands.relayRebootDescription');
      default:
        return t('commands.selectionMessage');
    }
  };

  const submitCommand = () => {
    if (submitting || activityLocked) return;

    const relayNum = relay.relay_number ?? 0;
    const commandName = RELAY_COMMAND_BY_CHOICE[choice] ?? null;

    if (relayNum !== 0 && siteId !== 0 && commandName != null) {
      trackEvent('send-command', { site_id: siteId, command: commandName });
      dispatchCommands([{ key: commandName, params: { relay_number: relayNum } }]).then(result => {
        if (result.status === DISPATCH_STATUS.SUCCESS) {
          setErrors([]);
          return;
        }
        setErrors(
          result.messages.length
            ? result.messages
            : result.failures.map(
                failure => failure.detail || (failure.message_key ? t(failure.message_key) : t('general.error')),
              ),
        );
      });
    }
    setChoice(null);
  };

  const relayLoadTypeCaps = getLoadTypeCapabilities(loadTypeCapsById, relay.load_type?.id ?? 0);
  const primaryState = relay.power_state;
  const secondaryRelay = relay.secondary_relay ?? null;
  const secondaryState = secondaryRelay ? (secondaryRelay.power_state ?? null) : null;
  // For paired relays, show off/red if either relay is off
  const effectivePowerState =
    primaryState === null && secondaryState === null
      ? null
      : primaryState === true && (secondaryState === null || secondaryState === true);

  const position = onMobile ? 'top' : 'left';

  const commandChoiceSection = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <ToggleButtonGroup
        exclusive
        disabled={activityLocked || submitting}
        value={choice}
        onChange={(event, newValue) => {
          if (newValue !== null) toggleChoice(newValue)(); // Triggers the same handler
        }}
        sx={{ pb: 1 }}
      >
        <ToggleButton value="on">{t('sites.on')}</ToggleButton>
        <ToggleButton value="off">{t('sites.off')}</ToggleButton>
        {relayLoadTypeCaps.supports_reboot_command && <ToggleButton value="reboot">{t('sites.reboot')}</ToggleButton>}
      </ToggleButtonGroup>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InfoIcon fontSize="small" />
        <Typography variant="body2">{getDescription()}</Typography>
      </Box>
    </Box>
  );

  const actions = [
    <Button key={1} color="primary" onClick={clearErrors}>
      {t('actions.continue')}
    </Button>,
  ];

  return (
    <SimpleTwoButtonPopup
      btnClassName="btn dashboard-btn"
      btnStyle={{
        background: 'transparent',
        padding: 0,
        border: 0,
        margin: 0,
        display: 'inline-flex',
        cursor: activityLocked ? 'not-allowed' : 'pointer',
        opacity: activityLocked ? 0.55 : 1,
      }}
      title={t('sites.relayPowerCommands')}
      description={commandChoiceSection}
      submitClassName="btn btn-sm btn-success"
      submitText={t('actions.confirm')}
      onSubmit={submitCommand}
      canSubmit={choice != null && !submitting && !activityLocked}
      canOpen={!submitting && !activityLocked}
      onOpenChange={onOpenChange}
      display={
        <>
          {displayOverride ?? (
            <div
              style={{
                borderRadius: stateColor ? '9px' : '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: stateColor
                  ? alpha(stateColor, 0.16)
                  : effectivePowerState === null
                    ? null
                    : effectivePowerState === true
                      ? green[500]
                      : red[700],
                color: stateColor ? stateColor : effectivePowerState === null ? undefined : 'white',
                border: !stateColor && effectivePowerState !== null ? '1px solid white' : null,
                // Bottom-lip inset — the theme's action-button signature (see
                // MuiButton contained/soft) — marks this icon as pressable,
                // unlike the flat non-interactive power-state badge.
                boxShadow: stateColor ? `inset 0 -3px 0 0 ${alpha(stateColor, 0.3)}` : undefined,
              }}
            >
              <PowerIcon style={{ fontSize: 20 }} />
            </div>
          )}
          {errors.length > 0 && (
            <Dialog open={true} onClose={clearErrors}>
              <DialogTitle sx={{ backgroundColor: red[700], color: 'white' }}>{t('general.error')}</DialogTitle>
              <DialogContent dividers>
                <div>
                  {errors.map((err, index) => (
                    <div key={index}>{err}</div>
                  ))}
                </div>
              </DialogContent>
              <DialogActions>{actions}</DialogActions>
            </Dialog>
          )}
        </>
      }
      placement={position}
      errorText=""
    />
  );
}

export default RelayCommandsPopup;
